import React, { useRef, useState } from 'react';
import { ArrowLeft, CircleAlert, CircleCheck, Download, FileSpreadsheet, Upload as UploadIcon } from 'lucide-react';
import { StorefrontService } from '../../../core/services/storefront.service';

interface Props { storefront_id: number; onDone: () => void; }

const CSV_HEADERS = [
    'sku', 'product_title', 'product_description', 'product_url',
    'price', 'discount_price', 'currency', 'category', 'tags',
    'commission_percent', 'in_stock',
    'image_url_1', 'image_url_2', 'image_url_3', 'image_url_4', 'image_url_5'
].join(',');

const CSV_SAMPLE = [
    'SKU-001', 'Baby Face Cream 200ml', 'Gentle moisturizing cream for babies',
    'https://mamaearth.in/products/baby-face-cream', '349.00', '299.00', 'INR',
    'Skincare', 'baby|cream|moisturizer', '10', '1',
    'https://cdn.mamaearth.in/img1.jpg', '', '', '', ''
].join(',');

export const BulkProductUpload: React.FC<Props> = ({ storefront_id, onDone }) => {
    const fileRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [alert, setAlert] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

    const downloadTemplate = () => {
        const csvContent = `${CSV_HEADERS}\n${CSV_SAMPLE}`;
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'storefront_products_template.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setAlert(null);
        setResult(null);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('storefront_id', String(storefront_id));
            const res = await StorefrontService.bulkUpload(formData);
            if (res?.status === 1) {
                setResult(res.data);
                setAlert({ type: 'success', msg: res.info });
            } else {
                setAlert({ type: 'error', msg: res?.info || 'Upload failed' });
            }
        } catch {
            setAlert({ type: 'error', msg: 'Something went wrong during upload' });
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="brd-container--narrow">
            <div className="brd-page-head">
                <button type="button" className="brd-icon-btn brd-icon-btn--filled" onClick={onDone} aria-label="Back to products" disabled={uploading}>
                    <ArrowLeft size={20} />
                </button>
                <div className="brd-page-head__text">
                    <h2 className="brd-page-head__title">Bulk Product Upload</h2>
                    <p className="brd-page-head__sub">Upload a CSV with your product catalogue. Start from our template.</p>
                </div>
                <button type="button" className="brd-btn brd-btn--outline" onClick={downloadTemplate}><Download size={16} /> CSV Template</button>
            </div>

            <section className="brd-section">
                <h3 className="brd-section__title">Columns</h3>
                <ul className="brd-store-cols">
                    <li><span className="brd-pill brd-pill--error">Required</span> product_title, product_url, price</li>
                    <li><span className="brd-pill brd-pill--muted">Optional</span> sku (auto-generated from the title if left blank), product_description, discount_price, currency (default INR), category, tags (pipe-separated), commission_percent, in_stock (1/0), image_url_1 … image_url_5</li>
                </ul>
                <p className="brd-field__hint" style={{ margin: '10px 0 0' }}>Duplicate SKUs are skipped (not updated). Max 5,000 rows per upload.</p>
            </section>

            <section className="brd-section">
                <button type="button" className="brd-upload" onClick={() => fileRef.current?.click()}>
                    <span className="brd-upload__icon">{file ? <FileSpreadsheet size={20} /> : <UploadIcon size={20} />}</span>
                    <span className="brd-upload__title">{file ? file.name : 'Select a CSV file'}</span>
                    <span className="brd-upload__hint">{file ? 'Tap to choose a different file' : '.csv only'}</span>
                </button>
                <input ref={fileRef} type="file" accept=".csv" hidden
                    onChange={e => { setFile(e.target.files?.[0] || null); setResult(null); setAlert(null); }} />

                {file && (
                    <button type="button" className="brd-btn brd-btn--primary brd-btn--lg brd-btn--block" style={{ marginTop: 14 }}
                        onClick={handleUpload} disabled={uploading} aria-busy={uploading}>
                        {uploading ? <><span className="brd-spinner brd-spinner--sm brd-spinner--light" /> Uploading…</> : `Upload ${file.name}`}
                    </button>
                )}

                {alert && (
                    <div className={`brd-banner ${alert.type === 'error' ? 'brd-banner--error' : 'brd-banner--info'}`} style={{ marginTop: 14 }} role="status">
                        {alert.type === 'error' ? <CircleAlert size={16} /> : <CircleCheck size={16} />}
                        <span>{alert.msg}</span>
                    </div>
                )}

                {result && (
                    <div style={{ marginTop: 6 }}>
                        <div className="brd-chip-row" style={{ marginBottom: 12 }}>
                            <span className="brd-pill brd-pill--success">{result.inserted} inserted</span>
                            {result.failed > 0 && <span className="brd-pill brd-pill--error">{result.failed} failed</span>}
                        </div>

                        {result.errors?.length > 0 && (
                            <>
                                <p className="brd-field__label" style={{ marginBottom: 6 }}>Errors</p>
                                <div className="brd-store-dtable">
                                    <table>
                                        <thead><tr><th>Row</th><th>SKU</th><th>Reason</th></tr></thead>
                                        <tbody>
                                            {result.errors.map((e: any, i: number) => (
                                                <tr key={i}><td>{e.row}</td><td>{e.sku}</td><td>{e.reason}</td></tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}

                        <button type="button" className="brd-btn brd-btn--secondary" style={{ marginTop: 14 }} onClick={onDone}>View Products</button>
                    </div>
                )}
            </section>
        </div>
    );
};
